import React from 'react';
import { Calendar, User, ArrowRight } from 'lucide-react';

const Gallery = () => {
  const galleryItems = [
    {
      title: 'Heavy Machinery Restoration',
      before: 'Severely corroded excavator',
      after: 'Fully restored with protective coating',
      category: 'Industrial',
      image: '/images/rusty train.png'
    },
    {
      title: 'Marine Equipment Protection',
      before: 'Salt-damaged dock hardware',
      after: 'Marine-grade corrosion protection applied',
      category: 'Marine',
      image: '/images/rustyship.png'
    },
    {
      title: 'Automotive Fleet Treatment',
      before: 'Rust-damaged truck chassis',
      after: 'Complete rust removal and undercoating',
      category: 'Automotive',
      image: '/images/rusty car.png'
    },
    {
      title: 'Structural Steel Protection',
      before: 'Corroded building framework',
      after: 'Long-term protective barrier system',
      category: 'Structural',
      image: '/images/rusty beams.png'
    },
    {
      title: 'Pipeline Maintenance',
      before: 'Corroded industrial pipeline',
      after: 'Fully protected with anti-corrosion coating',
      category: 'Industrial',
      image: '/images/rust 1.png'
    },
    {
      title: 'Agricultural Equipment',
      before: 'Weather-damaged farm machinery',
      after: 'Restored and protected for outdoor use',
      category: 'Agricultural',
      image: '/images/rusty train.png'
    },
  ];

  const blogStories = [
    {
      title: 'The $50,000 Save: How We Rescued a Mining Excavator',
      excerpt: 'A mining company thought their excavator was destined for the scrapyard. Here\'s how our team brought it back to life and saved them a fortune.',
      author: 'Mike Rodriguez',
      date: 'March 15, 2024',
      readTime: '5 min read',
      category: 'Case Study',
      image: '/images/rusty train.png'
    },
    {
      title: 'Marine Rust: Why Salt Water is Your Equipment\'s Worst Enemy',
      excerpt: 'Understanding the science behind saltwater corrosion and the specialized techniques we use to combat it in marine environments.',
      author: 'Sarah Chen',
      date: 'March 10, 2024',
      readTime: '7 min read',
      category: 'Education',
      image: '/images/rustyship.png'
    },
    {
      title: 'Fleet Manager\'s Guide: Preventing Rust in Commercial Vehicles',
      excerpt: 'Essential tips for fleet managers to extend vehicle life and reduce maintenance costs through proper rust prevention strategies.',
      author: 'Tom Wilson',
      date: 'March 5, 2024',
      readTime: '6 min read',
      category: 'Guide',
      image: '/images/rusty car.png'
    },
    {
      title: 'Emergency Response: 24-Hour Pipeline Restoration',
      excerpt: 'When a critical pipeline developed severe corrosion overnight, our emergency team responded immediately. Here\'s what happened.',
      author: 'Jake Martinez',
      date: 'February 28, 2024',
      readTime: '4 min read',
      category: 'Case Study',
      image: '/images/rusty beams.png'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center w-full bg-gray-900 dark:bg-black">
        
        <div className="relative z-10 px-6 lg:px-8 py-20 text-center text-white w-full max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Project <span className="text-orange-400">Gallery</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            See the dramatic transformations we achieve with our rust removal and protection services
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              Before & After Transformations
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Real projects showcasing our expertise in rust removal and protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryItems.map((item, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                {/* Image container: fixed aspect ratio so photos fill the slot */}
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-200 dark:bg-gray-800">
                  <img
                    src={item.image}
                    alt={`${item.title} - Before and After`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm mb-2 text-orange-600 dark:text-orange-400 font-semibold">
                    {item.category}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                  <div className="space-y-2 text-gray-600 dark:text-gray-300">
                    <div><strong>Before:</strong> {item.before}</div>
                    <div><strong>After:</strong> {item.after}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Stories Section */}
      <section className="py-20 px-6 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Success Stories & <span className="text-orange-600 dark:text-orange-400">Expert Insights</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Learn from our field experiences and get expert tips on rust prevention and treatment
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {blogStories.map((story, index) => (
              <article
                key={index}
                className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                {/* Image fills its slot using aspect ratio and object-cover */}
                <div className="relative w-full aspect-video overflow-hidden bg-gray-200 dark:bg-gray-800">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 px-3 py-1 rounded-full">
                      {story.category}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {story.readTime}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white line-clamp-2">
                    {story.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                    {story.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div className="text-sm">
                        <div className="text-gray-900 dark:text-white font-medium">{story.author}</div>
                        <div className="text-gray-500 dark:text-gray-400 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {story.date}
                        </div>
                      </div>
                    </div>
                    
                    <button className="flex items-center space-x-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors duration-200">
                      <span>Read More</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
              View All Stories
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
